import { ReactNode, useId } from "react";

import { useDroppable } from "@dnd-kit/core";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import { AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material";
import { AccordionKey } from "@shared";

import { DocumentGroup } from "@/api";
import { useDocumentDndContext } from "@/common/components/layout/components/dropzone/dnd/DocumentDndContext";
import { isDropAllowed } from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";
import { PersistentAccordion } from "@/common/components/persistent-accordion/PersistentAccordion";

interface Props {
  title: string;
  children: ReactNode;
  accordionKey: AccordionKey;
  zoneId: DocumentGroup;
}

export function DropzoneAccordion({ title, children, accordionKey, zoneId }: Props) {
  const id = useId();
  const { activeDrag } = useDocumentDndContext();

  const canDrop = activeDrag !== null && isDropAllowed(activeDrag.sourceZone, zoneId);
  const { setNodeRef, isOver } = useDroppable({ id: zoneId, disabled: !canDrop });

  const showHighlight = canDrop && isOver;
  const showHint = canDrop && !isOver;

  return (
    <PersistentAccordion
      storageKey={accordionKey}
      defaultExpanded
      ref={setNodeRef}
      disableGutters
      elevation={0}
      sx={(theme) => ({
        border: `1px solid ${showHighlight ? theme.palette.primary.main : showHint ? theme.palette.primary.light : theme.palette.fsw["blue-10"]}`,
        borderStyle: showHint ? "dashed" : "solid",
        borderRadius: 0.5,
        backgroundColor: showHighlight ? theme.palette.action.hover : undefined,
        transition: "border 150ms ease, background-color 150ms ease",
        "&::before": {
          display: "none",
        },
      })}
    >
      <AccordionSummary aria-controls={`${id}-content`} id={`${id}-header`} expandIcon={<ExpandIcon />}>
        <Typography variant="body2">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails id={`${id}-content`}>
        <Stack gap={3}>{children}</Stack>
      </AccordionDetails>
    </PersistentAccordion>
  );
}
